import React, { useContext, useState } from 'react';
import {
    Row,
    Col,
    Card,
    CardBody,
    CardTitle,
    CardText,
    Badge,
    Button,
} from 'reactstrap';
import { getDashColor } from '~/shared/components/icons';
import PayeeDetails from './PayeeDetails';
import ExpenseTable from './ExpenseTable';
import NotesSection from './NotesSection';
import { FundRequest } from '~/types/fundrequests';
import moment from 'moment';
import { useHistory } from 'react-router-dom';
import { FundRequestContext } from '~/context/fundrequests';
import { AdminContext } from '~/context/admin';
import { submitFundRequest } from '~/services/fundrequests';
import { SubmitForAppealModal } from './SubmitForAppealModal';
import RejectionReasonAlert from './RejectionReasonAlert';
import toast from 'react-hot-toast';
import SubmissionConfirm from './SubmissionConfirm';
import { getBadgeLabel, getBadgeClass } from '~/shared/utils';

const SubmissionConfirmation = ({
    setShowConfirmationPage,
    setSubmissionComplete
}: {
    setShowConfirmationPage?: (show: boolean) => void;
    setSubmissionComplete?: (complete: boolean) => void;
}) => {
    const [{ campaignId }, adminDispatch] = useContext(AdminContext);
    const [
        { fundRequest, expenseIndex, transactionsArray },
        fundRequestDispatch,
    ] = useContext(FundRequestContext);
    const [submitting, setSubmitting] = useState(false);
    const [openModal, setOpenModal] = useState<boolean>(false);
    const [showConfirmation, setShowConfirmation] = useState<boolean>(false);
    const [acknowledgementChecked, setAcknowledgementChecked] = useState<boolean>(false);
    const lastUpdated = moment(fundRequest?.updated_date).format('MM/DD/YY');
    const history = useHistory();

    const isDraft = fundRequest?.fund_request_status.value === 'draft';
    console.log(
        'fundRequest?.fund_request_status: ',
        fundRequest?.fund_request_status
    );

    const handleSubmitFundRequest = async () => {
        try {
            setSubmitting(true);
            const resp = await submitFundRequest(fundRequest.id, campaignId);
            if (resp.error) {
                throw new Error(resp.error);
            } else {
                toast.success('Fund request submitted!');
                setShowConfirmation(true);
            }
        } catch (error) {
            setSubmitting(false);
            console.log('error: ', error);
            if (error instanceof Error) {
                toast.error(error.message);
            } else if (typeof error === 'string') {
                toast.error(error);
            } else {
                toast.error('An unknown error occurred');
            }
            setShowConfirmation(false);
        }
    };

    const statusValue = fundRequest?.fund_request_status.value;
    const statusLabel = fundRequest?.fund_request_status.label;
    let badgeLabel = getBadgeLabel(statusValue, statusLabel);
    const badgeClass = getBadgeClass(badgeLabel, statusValue);

    return (
        <div style={{ display: 'flex', flexDirection: 'column', minHeight: 'calc(100vh - 120px)' }}>
            {showConfirmation ? (
                <SubmissionConfirm fundRequest={fundRequest} />
            ) : (
                <>
                    <div style={{ flex: 1 }}>
                    <div className='mb-4'>
                        <h1 className='h2'>Review Request</h1>
                        <div className='d-flex align-items-center flex-wrap'>
                            <Badge
                                color={`none`}
                                className={`${badgeClass} me-3`}
                            >
                                {badgeLabel}
                            </Badge>
                            <span className='text-muted me-3'>
                                Request ID{' '}
                                <span className='fw-bold'>
                                    {fundRequest.id}
                                </span>
                            </span>
                            {fundRequest.campaign_contact ? (
                                <>
                                    <span className='text-muted me-3'>
                                        Requested by:{' '}
                                        <span className='fw-bold'>
                                            {fundRequest.campaign_contact.first_name} {fundRequest.campaign_contact.last_name}
                                        </span>
                                    </span>
                                    {fundRequest.created_by?.name && (
                                        <span className='text-muted me-3'>
                                            Submitted by:{' '}
                                            <span className='fw-bold'>
                                                {fundRequest.created_by.name}
                                            </span>
                                        </span>
                                    )}
                                </>
                            ) : (
                                fundRequest.created_by?.name && (
                                    <span className='text-muted me-3'>
                                        Requested by:{' '}
                                        <span className='fw-bold'>
                                            {fundRequest.created_by.name}
                                        </span>
                                    </span>
                                )
                            )}
                            {fundRequest?.financial_coordinator &&
                             ['submitted', 'needs_info', 'paid', 'in_mip'].includes(statusValue) && (
                                <span className='text-muted me-3'>
                                    Financial Coordinator:{' '}
                                    <span className='fw-bold'>
                                        {fundRequest.financial_coordinator.first_name}{' '}
                                        {fundRequest.financial_coordinator.last_name}
                                    </span>
                                </span>
                            )}
                            <span className='text-muted'>
                                Last Updated:{' '}
                                <span className='fw-bold'>
                                    {lastUpdated}
                                </span>
                            </span>
                        </div>
                    </div>
                    {fundRequest?.partial_parent && (
                        <div className='alert alert-warning mb-4'>
                            <p className='mb-0'>
                                This fund request has been created as a remainder from partial disbursement of Fund Request{' '}
                                <strong>{fundRequest.partial_parent.id}</strong>. It will remain in a pending status until additional funds are available to disburse it.
                            </p>
                        </div>
                    )}
                    {fundRequest?.fund_request_status.value ===
                        'rejected' && fundRequest?.notes && (
                            <div className='mb-5'>
                                <RejectionReasonAlert
                                    infoNote={fundRequest?.notes?.find(note => 
                                        note.fund_request_status?.value === 'needs_info' ||
                                        (note.fund_request_status?.value === fundRequest.fund_request_status.value && !note.is_client_note)
                                    )?.note}
                                />
                            </div>
                        )}
                    <Row>
                        <Col lg={8}>
                            <Card
                                style={{
                                    background: getDashColor('supporters'),
                                }}
                            >
                                <CardBody>
                                    <Row>
                                        <Col style={{ marginBottom: 20 }}>
                                            <span
                                                className='me-3'
                                                style={{
                                                    fontSize: '1.8rem',
                                                }}
                                            >
                                                Request ID
                                                <p
                                                    style={{
                                                        display: 'inline',
                                                        fontWeight: 600,
                                                        marginLeft: 5,
                                                    }}
                                                >
                                                    {fundRequest?.id}
                                                </p>
                                            </span>
                                        </Col>
                                    </Row>
                                    <Row className=''>
                                        <Col>
                                            <PayeeDetails />
                                        </Col>
                                    </Row>
                                    <Row className='mt-4'>
                                        <Col>
                                            <ExpenseTable />
                                        </Col>
                                    </Row>
                                    {/* Notes moved to right column */}
                                    <Row className='mt-4'>
                                        <Col></Col>
                                    </Row>
                                </CardBody>
                            </Card>
                        </Col>
                        <Col lg={4}>
                            {/* Notes in right column */}
                            <Card
                                className='mt-4'
                                style={{
                                    background: '#ffffff',
                                    boxShadow: '0 8px 20px rgba(0,0,0,0.08)',
                                    borderRadius: 12,
                                }}
                            >
                                <CardBody>
                                    <NotesSection />
                                </CardBody>
                            </Card>

                            {/* Appeal card, conditionally shown */}
                            {(fundRequest?.fund_request_status.value === 'rejected' ||
                                fundRequest?.fund_request_status.value === 'appealed') &&
                                fundRequest?.can_appeal !== false && (
                                    <Card
                                        className='mt-4'
                                        style={{
                                            background: getDashColor(
                                                'pending_verification'
                                            ),
                                        }}
                                    >
                                        <CardBody>
                                            <CardTitle className='eyebrow'>
                                                Submit for Appeal
                                            </CardTitle>
                                            <CardText className='my-4'>
                                                If you would like to appeal this
                                                rejection, please submit a short
                                                description of your circumstances
                                                and why Help Hope Live should authorize an
                                                exception to our guidelines for this
                                                request. The Fund Request and your
                                                response will be reviewed by the
                                                Grant Committee and a final decision
                                                will be made within 10 business
                                                days.
                                            </CardText>
                                            <Button
                                                onClick={() => setOpenModal(true)}
                                                color='primary'
                                                disabled={
                                                    fundRequest?.fund_request_status.value === 'appealed'
                                                }
                                            >
                                                Submit for Appeal
                                            </Button>
                                        </CardBody>
                                    </Card>
                                )}
                        </Col>
                    </Row>
                    </div>
                    <div className='sticky-bottom'>
                        {isDraft && (
                            <div className='mb-3 d-flex align-items-start'>
                                <input
                                    type='checkbox'
                                    id='acknowledgement'
                                    checked={acknowledgementChecked}
                                    onChange={(e) => setAcknowledgementChecked(e.target.checked)}
                                    className='me-2 mt-1'
                                />
                                <label htmlFor='acknowledgement' className='text-muted' style={{ fontSize: '1rem' }}>
                                    I acknowledge that I have reviewed the information above and certify to the best of my knowledge that the information I have provided is true and accurate.
                                </label>
                            </div>
                        )}
                        <Button
                            onClick={() => {
                                if (isDraft) {
                                    setShowConfirmation(false);
                                    setShowConfirmationPage?.(false);
                                    setSubmissionComplete?.(false);
                                } else {
                                    history.push('/requests');
                                }
                            }}
                            color='link'
                        >
                            {isDraft ? 'Back to Edit' : 'Return to Requests'}
                        </Button>
                        {isDraft && (
                            <Button
                                onClick={handleSubmitFundRequest}
                                color='primary'
                                disabled={submitting || !acknowledgementChecked}
                            >
                                {submitting
                                    ? 'Submitting...'
                                    : 'Submit Request'}
                            </Button>
                        )}
                        {(fundRequest?.fund_request_status.value === 'rejected' ||
                            fundRequest?.fund_request_status.value === 'appealed') &&
                            fundRequest?.can_appeal !== false && (
                                <Button
                                    onClick={() => setOpenModal(true)}
                                    color='primary'
                                    disabled={
                                        fundRequest?.fund_request_status.value === 'appealed'
                                    }
                                >
                                    Submit for Appeal
                                </Button>
                            )}
                    </div>
                    <SubmitForAppealModal
                        isOpen={openModal}
                        toggle={() => setOpenModal(!openModal)}
                    />
                </>
            )}
        </div>
    );
};

export default SubmissionConfirmation;
